import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import LoadingSpinner from 'components/LoadingSpinner';
import { Context } from 'components/Store';
import useProduct from 'hooks/useProduct';
import {
  SET_SHOW_TOAST_REMOVE_PRODUCT,
  SET_TOAST_MESSAGE,
  SET_TOAST_TYPE
} from 'reducers/reducer';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import { getServerErrorMessage } from 'helpers/errorHelpers';
import Toast from 'components/Toast';
import EditProductDrawer from 'screens/ProductList/components/EditProductDrawer';
import useInventoryProduct from 'screens/ProductList/hooks/useInventoryProduct';
import ProductDetailInfo from './components/ProductDetailInfo';
import { emptyValue } from '../helpers/tableConstants';
import ProductDetailTabs from './components/ProductDetailTabs';
import {
  buildCustomProductPayload,
  buildMasterlistProductPayload
} from '../helpers/formValuesToPayload';
import { ProductDetailsProvider } from './context/ProductDetailsProvider';

const ProductDetails = () => {
  const { id: productId } = useParams();
  const { getProduct, product } = useProduct();

  const {
    addUpdateListPrice,
    updateCustomProduct,
    removeProduct,
    setRemoveError
  } = useInventoryProduct();
  const [{ removeProductFailure }, dispatch] = useContext(Context);

  const [toastMessage, setToastMessage] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState(null);
  const [productToEdit, setProductToEdit] = useState(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editProductHeader, setEditProductHeader] = useState('');
  const [productToRemove, setProductToRemove] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const history = useHistory();

  const refreshProducts = () => {
    getProduct(productId);
  };

  const handleOnEdit = prod => {
    setProductToEdit(prod);
    setShowEditDrawer(true);
    if (product?.custom) {
      setEditProductHeader('Edit Custom Product');
    } else {
      setEditProductHeader('Edit Product');
    }
  };

  const handleRemove = async () => {
    try {
      await removeProduct(productToRemove);
      refreshProducts();
    } finally {
      if (!removeProductFailure) {
        dispatch({
          type: SET_TOAST_MESSAGE,
          payload: 'Product removed'
        });
        dispatch({
          type: SET_TOAST_TYPE,
          payload: 'success'
        });
        dispatch({
          type: SET_SHOW_TOAST_REMOVE_PRODUCT,
          payload: true
        });
      } else {
        dispatch({
          type: SET_TOAST_MESSAGE,
          payload: 'Unable to remove product.'
        });
        dispatch({
          type: SET_TOAST_TYPE,
          payload: 'error'
        });
        dispatch({
          type: SET_SHOW_TOAST_REMOVE_PRODUCT,
          payload: true
        });
      }
      history.goBack();
    }
  };

  const handleEdit = async values => {
    // Neither save rejects on failure, so the outcome has to be read back
    // rather than caught.
    let failure = null;
    if (values.custom) {
      const result = await updateCustomProduct(
        buildCustomProductPayload(values)
      );
      // A rejection can carry no error object, so it needs its own stand-in
      if (!result?.success)
        failure = result?.error ?? new Error('Unable to save changes');
    } else {
      await addUpdateListPrice(
        buildMasterlistProductPayload(values),
        undefined,
        err => {
          failure = err ?? new Error('Unable to save changes');
        }
      );
    }

    setToastType(failure ? 'error' : 'success');
    setToastMessage(
      failure
        ? getServerErrorMessage(failure) ?? 'Unable to save changes'
        : 'Changes saved.'
    );
    setShowToast(true);

    // A rejected save leaves the drawer open so the reason can be acted on,
    // and the list is left alone since nothing actually changed.
    if (failure) return;

    refreshProducts();
    setShowEditDrawer(false);
  };

  useEffect(() => {
    getProduct(productId);
  }, [getProduct, productId]);

  return (
    <ProductDetailsProvider>
      <div className="h-full w-full flex flex-col" data-testid="product-view">
        <Breadcrumb disabled>
          <Breadcrumb.Item
            title="Products"
            value={product?.productName ?? emptyValue.dashLine}
            isLast
          />
        </Breadcrumb>
        {!product ? (
          <div
            className="flex flex-auto items-center justify-center"
            data-testid="product-loading"
          >
            <LoadingSpinner message="" size={50} label="Loading product" />
          </div>
        ) : (
          <>
            <ProductDetailInfo
              setShowRemoveModal={setShowRemoveModal}
              setProductToRemove={setProductToRemove}
              productToRemove={productToRemove}
              showRemoveModal={showRemoveModal}
              handleRemove={handleRemove}
              refreshProducts={refreshProducts}
              setShowToast={setShowToast}
              setToastMessage={setToastMessage}
              setToastType={setToastType}
              product={product}
              onEdit={handleOnEdit}
            />
            <ProductDetailTabs product={product} />
          </>
        )}
        <EditProductDrawer
          handleEdit={handleEdit}
          product={productToEdit}
          visible={showEditDrawer}
          onClose={() => setShowEditDrawer(false)}
          editProductHeader={editProductHeader}
        />
        <Toast
          type={toastType}
          isBold
          open={showToast}
          title={toastMessage}
          timeout={5000}
          onClose={() => {
            setShowToast(false);
            setRemoveError(null);
          }}
        />
      </div>
    </ProductDetailsProvider>
  );
};
export default ProductDetails;
